/**
 * DRPX Courier — Navigation-Hook
 *
 * Tab-Routing, Badges, Mobile-Menu, Keyboard-Shortcuts.
 * Leitet Tab-IDs auf React Router Pfade um. Berechnet Nav-Badges
 * aus Aufträgen/Bewertungen.
 *
 * @module hooks/useNavigation
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { countKycNeedsAttention } from '../lib/kycDocs';
import { hatMarktplatzZugang } from '../lib/aboAccess.js';
import { db } from '../lib/supabase.js';
import { tabToPath, pathToTab } from '../config/routes.js';
import { istFirmenZugang, istMitarbeiter, istBieter, chatSeite, personId, darf, istChef } from '../lib/zugang.js';
import type { Profile, Auftrag, Bewertung, TranslateFn } from '../types.js';

// ════════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════════

type OpenModalFn = (type: string, data: unknown) => void;

interface NavTab {
  id?: string;
  icon?: string;
  label?: string;
  locked?: boolean;
  divider?: boolean;
  divIcon?: string;
}

interface UseNavigationOptions {
  user: Profile | null;
  auftraege: Auftrag[];
  bewertungen: Bewertung[];
  users: Profile[];
  t: TranslateFn;
  pendingZertsTotal: number;
  openModal: OpenModalFn;
}

interface UseNavigationReturn {
  tab: string;
  nav: (target: string) => void;
  navTabs: NavTab[];
  navBadges: Record<string, number>;
  mobileMenu: boolean;
  setMobileMenu: (v: boolean) => void;
  bottomSheet: boolean;
  setBottomSheet: (v: boolean) => void;
  chatUngeleseneAktiv: Record<string, number>;
  ladeChatUngelesen: () => void;
}

// ════════════════════════════════════════════════════════════════════
// Hook
// ════════════════════════════════════════════════════════════════════

export function useNavigation({
  user, auftraege, bewertungen, users, t, pendingZertsTotal, openModal,
}: UseNavigationOptions): UseNavigationReturn {
  const navigate = useNavigate();
  const location = useLocation();

  // ═══ Tab aus URL ableiten ═══
  const tab = useMemo(function () {
    return pathToTab(location.pathname);
  }, [location.pathname]);

  const [mobileMenu, setMobileMenu] = useState(false);
  const [bottomSheet, setBottomSheet] = useState(false);

  // ═══ nav() — akzeptiert Tab-IDs UND Pfade ═══
  const nav = useCallback(function (target: string) {
    const path = target.startsWith('/') ? target : tabToPath(target);
    navigate(path);
  }, [navigate]);

  // ═══ Chat ungelesen ═══
  const [chatUngelesen, setChatUngelesen] = useState<Record<string, number>>({});
  const chatUngeleseneAktiv = useMemo(function () {
    const AKTIV = ['vergeben', 'beladen', 'unterwegs', 'angekommen', 'geliefert', 'abgeschlossen'];
    const aktiveIds = new Set(
      auftraege
        .filter(function (a) { return AKTIV.includes(a.status); })
        .map(function (a) { return a.id; }),
    );
    const f: Record<string, number> = {};
    Object.keys(chatUngelesen).forEach(function (id) {
      if (aktiveIds.has(id)) f[id] = chatUngelesen[id];
    });
    return f;
  }, [auftraege, chatUngelesen]);

  const ladeChatUngelesen = useCallback(function () {
    // Admin chattet nicht. Beide Seiten (Auftraggeber/Auftragnehmer) zählt
    // die Abfrage selbst (17.10.2026).
    if (!user || !chatSeite(user)) return;
    db.getUngeleseneChatsProAuftrag(user.id, personId(user)).then(function (r) {
      setChatUngelesen(r.data || {});
    });
  }, [user]);

  useEffect(function () {
    ladeChatUngelesen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.id]);

  // ═══ Nav Tabs ═══
  const navTabs = useMemo(function (): NavTab[] {
    if (!user) return [];
    const _gated = !hatMarktplatzZugang(user) && user.rolle !== 'admin';
    if (istFirmenZugang(user)) {
      const firmaTabs: NavTab[] = [
        { id: 'dashboard', icon: 'chart', label: t('dashboard') },
        { id: 'auftraege', icon: 'package', label: t('auftraege'), locked: _gated },
        { id: 'adressbuch', icon: 'pin', label: t('abTitel') },
        { id: 'abrechnung', icon: 'receipt', label: t('abrechnung'), locked: _gated },
        { id: 'bewertungen', icon: 'star', label: t('bewUebersicht') },
        { id: 'abo', icon: 'diamond', label: t('abo') },
        { id: 'favoriten', icon: 'heart', label: t('favoriten') },
        // DRPX-Pro (Migration 141): Team nur für den Chef einer Pro-Firma
        // Jeder Chef sieht „Team“ — ohne DRPX-Pro steht dort das Pro-Angebot (Lieferung 3a)
        ...(istChef(user) ? [{ id: 'team', icon: 'users', label: t('teamTitel') }] : []),
        { id: 'profil', icon: 'user', label: t('profilSeite') },
        { id: 'roteListe', icon: 'blocked', label: t('roteListe') },
        { id: 'ueberuns', icon: 'globe-web', label: t('ueberuns') },
      ];
      // DRPX-Pro (16.10.2026): Chef-Bereiche fehlen beim Mitarbeiter —
      // Abo und Rote Liste (Bearbeiten) verwaltet der Chef. Die Seiten sind
      // zusätzlich per Adresse gesperrt (KeinMitarbeiterRoute).
      // Abrechnung nur mit dem Recht „Abrechnung sehen" (Migration 141).
      const sichtbar = istMitarbeiter(user)
        ? firmaTabs.filter(function (x) {
            if (x.id === 'abrechnung') return darf(user, 'abrechnung');
            return x.id !== 'abo' && x.id !== 'roteListe';
          })
        : firmaTabs;
      // Spedition (Migration 105/106): Eine freigeschaltete Firma darf
      // zusätzlich auf fremde Aufträge bieten. Dann wird dieselbe
      // Marktplatz-Sektion wie beim Fahrer eingeblendet (Bieten + meine
      // Angebote + gewonnene Aufträge als Auftragnehmer). Erscheint
      // automatisch in Desktop-Sidebar und mobilem Drawer.
      if (user.darfBieten) {
        sichtbar.push(
          { divider: true, label: t('drpxMarktplatz'), divIcon: 'building', locked: _gated },
          { id: 'marktplatz', icon: 'package', label: t('marktplatz'), locked: _gated },
          { id: 'meine', icon: 'offer-incoming', label: t('meine'), locked: _gated },
          { id: 'meineauftraege', icon: 'truck', label: t('laufendeAuftraege'), locked: _gated },
          // Zertifikate & Nachweise (16.08.2026). Fehlte bisher, obwohl die
          // Firma mit `darfBieten` denselben Marktplatz-Block bekommt wie ein
          // Fahrer. Folge: Bei Aufträgen mit Pflicht-Ausstattung (Gefahrgut →
          // ADR, Pharma → GDP) meldete `getFehlendeZertifikate` immer ALLE
          // Nachweise als fehlend — die Spedition sah bei jedem Gebot die rote
          // Warnung und konnte nichts dagegen tun, weil ihr die Upload-Seite
          // fehlte. Beim Auftraggeber blieb dauerhaft „Zertifikat fehlt" am
          // Angebot stehen.
          //
          // Nichts stand dem entgegen: Tabelle und Storage prüfen nur
          // `auth.uid()`, die Route hängt an `GatedRoute` (Abo + Verifizierung,
          // rollenneutral), und `FahrerZertifikate.tsx` enthält keine
          // Rollenabfrage und keinen Fahrer-Context. Inhaltlich passt es
          // ohnehin: EU-Lizenz, MiLoG-Erklärung, Zollzulassung und
          // Transportversicherung sind Firmen-Nachweise, keine persönlichen
          // Fahrerscheine.
          { id: 'zertifikate', icon: 'shield', label: t('zertifikate'), locked: _gated },
        );
        // Zertifikate hochladen ist Chefsache (DRPX-Pro, 16.10.2026). Lesen
        // darf der Mitarbeiter (Migration 139) — für die Prüfung beim Bieten.
        if (istMitarbeiter(user)) sichtbar.pop();
      }
      return sichtbar;
    }
    if (user.rolle === 'fahrer')
      return [
        { divider: true, label: t('hcMeinGeschaeft'), divIcon: 'briefcase' },
        { id: 'dashboard', icon: 'chart', label: t('dashboard') },
        { id: 'eigeneauftraege', icon: 'clipboard', label: t('auftraegeSP') },
        { id: 'kunden', icon: 'users', label: t('meineKunden') },
        { id: 'fahrtenbuch', icon: 'notebook', label: t('fahrtenbuch') },
        { id: 'finanzen', icon: 'money-bag', label: t('finanzen') },
        { id: 'rechnungen-eigen', icon: 'receipt', label: t('rechnungen') },
        { id: 'tourenplaner', icon: 'map', label: t('tpTitel') },
        { divider: true, label: t('drpxMarktplatz'), divIcon: 'building', locked: _gated },
        { id: 'marktplatz', icon: 'package', label: t('marktplatz'), locked: _gated },
        { id: 'meine', icon: 'offer-incoming', label: t('meine'), locked: _gated },
        { id: 'meineauftraege', icon: 'truck', label: t('laufendeAuftraege'), locked: _gated },
        { id: 'zertifikate', icon: 'shield', label: t('zertifikate'), locked: _gated },
        { id: 'auszahlung', icon: 'receipt', label: t('abrechnung'), locked: _gated },
        { divider: true, label: t('konto'), divIcon: 'user' },
        { id: 'profil', icon: 'user', label: t('profilSeite') },
        { id: 'bewertungen', icon: 'star', label: t('bewUebersicht') },
        { id: 'roteListe', icon: 'blocked', label: t('roteListe') },
        { id: 'abo', icon: 'diamond', label: t('abo') },
        { id: 'ueberuns', icon: 'globe-web', label: t('ueberuns') },
      ];
    return [
      { id: 'admin', icon: 'settings', label: t('dashboard') },
      { id: 'admin-health', icon: 'shield', label: 'Health' },
      { id: 'admin-funnel', icon: 'chart', label: 'Funnel' },
      { id: 'admin-blacklist', icon: 'cross-circle', label: 'Blacklist' },
      { id: 'admin-users', icon: 'users', label: t('nutzer') },
      { id: 'admin-auftraege', icon: 'package', label: t('auftraege') },
      { id: 'admin-bewertungen', icon: 'star', label: t('bewUebersicht') },
      { id: 'admin-abos', icon: 'diamond', label: t('abos') },
      { id: 'admin-reengagement', icon: 'mail', label: 'Re-Engagement' },
      { id: 'admin-map', icon: 'map', label: 'Karte' },
      { id: 'admin-usage', icon: 'chart', label: 'Aktivität' },
      { id: 'admin-monitoring', icon: 'shield', label: 'Monitoring' },
      { id: 'admin-finanzen', icon: 'money-bag', label: t('finanzen') },
      { id: 'admin-settings', icon: 'wrench', label: t('adminPlattformSettings') },
      { id: 'admin-audit', icon: 'scroll', label: t('adminAuditLog') },
      { id: 'ueberuns', icon: 'globe-web', label: t('ueberuns') },
    ];
  }, [user, t]);

  // ═══ Nav Badges ═══
  const navBadges = useMemo(function () {
    if (!user) return {};
    const b: Record<string, number> = {};
    const chatAnzahl = Object.keys(chatUngeleseneAktiv).length;
    if (istFirmenZugang(user)) {
      b['auftraege'] =
        auftraege.filter(function (a) {
          return a.firmaId === user.id && a.status === 'angebote';
        }).length + chatAnzahl;
      b['bewertungen'] = auftraege.filter(function (a) {
        return (
          a.firmaId === user.id &&
          a.status === 'abgeschlossen' &&
          !bewertungen.find(function (bw) {
            return bw.auftragId === a.id && bw.vonId === user.id;
          })
        );
      }).length;
    }
    // Marktplatz-Badges: Fahrer ODER freigeschaltete Spedition (Firma
    // mit darfBieten). Die Zähl-Logik ist bereits user.id-basiert und
    // gilt damit unverändert für beide. Eine geflaggte Firma durchläuft
    // zusätzlich den firma-Block oben (auftraege/bewertungen-Badges).
    // Mitarbeiter: `user.id` ist die Firma (Weg B) — gleiche Zahlen wie beim Chef.
    if (istBieter(user)) {
      b['marktplatz'] = auftraege.filter(function (a) {
        return (a.status === 'offen' || a.status === 'angebote') && a.firmaId !== user.id;
      }).length;
      b['meine'] = auftraege.filter(function (a) {
        // Status-Filter konsistent zu MeineAngebote.tsx Liste:
        // storniert/abgeschlossen/abgelaufen werden ausgeschlossen,
        // sonst Phantom-Counter (siehe Bug-Fix 28.04.2026 nach
        // Migration 061, der abgelaufene Aufträge entstehen lässt).
        if (a.status === 'storniert' || a.status === 'abgeschlossen' || a.status === 'abgelaufen') return false;
        return (a.angebote || []).some(function (ang) {
          return ang.fahrerId === user.id && !ang.akzeptiert;
        });
      }).length;
      b['meineauftraege'] =
        auftraege.filter(function (a) {
          return (
            ['vergeben', 'beladen', 'unterwegs', 'angekommen', 'geliefert'].includes(a.status) &&
            (a.angebote || []).some(function (ang) {
              return ang.fahrerId === user.id && ang.akzeptiert;
            })
          );
        }).length + chatAnzahl;
    }
    if (user.rolle === 'admin') {
      // Die Zahl an „Nutzer" bündelt seit dem 16.08.2026 beide Arten von
      // Prüfarbeit: offene Zertifikate UND offene Verifizierungs-Dokumente.
      // Das eigene Verifizierungs-Menü ist entfallen; ohne diese Bündelung
      // gäbe es für KYC gar keine Zahl mehr in der Leiste.
      //
      // Bewusste Änderung der Zählweise: Vorher wurde `kycStatus === 'pending'`
      // gezählt — das trifft auch auf frisch registrierte Nutzer zu, die noch
      // nichts hochgeladen haben. Die Leiste zeigte dann z.B. „3", obwohl im
      // Panel „ZU PRÜFEN 0" stand. Eine Zahl, die Arbeit meldet wo keine ist,
      // gewöhnt man sich ab zu beachten. Gezählt wird jetzt, was wirklich auf
      // dem Tisch liegt: ein hochgeladenes Dokument ohne Bewertung.
      b['admin-users'] = pendingZertsTotal + countKycNeedsAttention(users);
      b['admin-auftraege'] = auftraege.filter(function (a) {
        return a.status === 'offen' || a.status === 'angebote';
      }).length;
    }
    return b;
  }, [user, auftraege, bewertungen, users, pendingZertsTotal, chatUngeleseneAktiv]);

  // ═══ Keyboard Shortcuts (Ctrl+Buchstabe) ═══
  useEffect(function () {
    if (!user) return;
    const currentUser = user;
    function handleKey(e: KeyboardEvent) {
      if (!e.ctrlKey && !e.metaKey) return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;
      if (target.isContentEditable) return;
      const key = e.key.toLowerCase();
      if (key === 'n' && istFirmenZugang(currentUser) && openModal) {
        e.preventDefault();
        openModal('neuerAuftrag', null);
      }
      if (key === 'm' && currentUser.rolle === 'fahrer') {
        e.preventDefault();
        nav('marktplatz');
      }
      if (key === 'e') {
        e.preventDefault();
        if (istFirmenZugang(currentUser)) {
          if (darf(currentUser, 'abrechnung')) nav('abrechnung');
        }
        else if (currentUser.rolle === 'fahrer') nav('auszahlung');
      }
      if (key === 'p') {
        e.preventDefault();
        nav('profil');
      }
    }
    window.addEventListener('keydown', handleKey);
    return function () {
      window.removeEventListener('keydown', handleKey);
    };
  }, [user, nav, openModal]);

  return {
    tab, nav, navTabs, navBadges,
    mobileMenu, setMobileMenu,
    bottomSheet, setBottomSheet,
    chatUngeleseneAktiv, ladeChatUngelesen,
  };
}
